import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Building2, Phone, Mail, Send, Loader2, CheckCircle,
  Package, Truck, Clock, Globe, ShieldCheck, Users, ArrowRight, ChevronDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const countryCodes = [
  { code: '+234', country: 'Nigeria' },
  { code: '+233', country: 'Ghana' },
  { code: '+254', country: 'Kenya' },
  { code: '+27', country: 'South Africa' },
  { code: '+44', country: 'United Kingdom' },
  { code: '+1', country: 'United States' },
  { code: '+86', country: 'China' },
  { code: '+91', country: 'India' },
  { code: '+971', country: 'UAE' },
  { code: '+966', country: 'Saudi Arabia' },
  { code: '+49', country: 'Germany' },
  { code: '+33', country: 'France' },
  { code: '+39', country: 'Italy' },
  { code: '+34', country: 'Spain' },
  { code: '+31', country: 'Netherlands' },
  { code: '+32', country: 'Belgium' },
  { code: '+41', country: 'Switzerland' },
  { code: '+46', country: 'Sweden' },
  { code: '+47', country: 'Norway' },
  { code: '+55', country: 'Brazil' },
  { code: '+52', country: 'Mexico' },
  { code: '+57', country: 'Colombia' },
  { code: '+81', country: 'Japan' },
  { code: '+82', country: 'South Korea' },
  { code: '+61', country: 'Australia' },
  { code: '+64', country: 'New Zealand' },
  { code: '+65', country: 'Singapore' },
  { code: '+60', country: 'Malaysia' },
  { code: '+62', country: 'Indonesia' },
  { code: '+63', country: 'Philippines' },
  { code: '+20', country: 'Egypt' },
  { code: '+212', country: 'Morocco' },
  { code: '+225', country: 'Ivory Coast' },
  { code: '+237', country: 'Cameroon' },
  { code: '+251', country: 'Ethiopia' },
  { code: '+255', country: 'Tanzania' },
  { code: '+256', country: 'Uganda' },
  { code: '+260', country: 'Zambia' },
  { code: '+263', country: 'Zimbabwe' },
  { code: '+7', country: 'Russia' },
  { code: '+90', country: 'Turkey' },
  { code: '+48', country: 'Poland' },
  { code: '+351', country: 'Portugal' },
  { code: '+353', country: 'Ireland' },
  { code: '+45', country: 'Denmark' },
  { code: '+358', country: 'Finland' },
  { code: '+43', country: 'Austria' },
  { code: '+30', country: 'Greece' },
  { code: '+974', country: 'Qatar' },
  { code: '+968', country: 'Oman' },
  { code: '+973', country: 'Bahrain' },
  { code: '+965', country: 'Kuwait' },
  { code: '+962', country: 'Jordan' },
  { code: '+961', country: 'Lebanon' },
  { code: '+92', country: 'Pakistan' },
  { code: '+880', country: 'Bangladesh' },
  { code: '+94', country: 'Sri Lanka' },
  { code: '+66', country: 'Thailand' },
  { code: '+84', country: 'Vietnam' },
  { code: '+852', country: 'Hong Kong' },
  { code: '+56', country: 'Chile' },
  { code: '+54', country: 'Argentina' },
  { code: '+51', country: 'Peru' },
];

const Wholesale = () => {
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [refNumber, setRefNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [dialCode, setDialCode] = useState('+234');
  const [form, setForm] = useState({
    business_name: '', contact_name: '', email: '', phone: '',
    business_type: '', products_interested: '',
    estimated_quantity: '', delivery_frequency: '',
    delivery_location: '', message: ''
  });

  const requestedProduct = searchParams.get('product') || '';
  const requestedQuantity = searchParams.get('quantity') || '';

  useEffect(() => {
    if (!requestedProduct) return;
    setForm((current) => ({
      ...current,
      products_interested: requestedProduct,
      estimated_quantity: requestedQuantity || current.estimated_quantity,
    }));
  }, [requestedProduct, requestedQuantity]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        phone: `${dialCode}${form.phone.replace(/\D/g, '')}`,
        product_id: searchParams.get('productId') || '',
        source: searchParams.get('source') || 'wholesale-page',
      };
      const res = await axios.post(`${API}/wholesale/inquiry`, payload);
      if (res.data.success) {
        setRefNumber(res.data.inquiry_number);
        setSubmitted(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit inquiry');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="wholesale-success">
        <div className="py-24 px-4 text-center max-w-lg mx-auto">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Inquiry Received!</h1>
          <p className="text-gray-600 mb-2">Thank you for your interest in our wholesale program.</p>
          <p className="text-sm text-gray-500 mb-6">
            Your reference number: <span className="font-mono font-bold text-green-700">{refNumber}</span>
          </p>
          <p className="text-gray-600 mb-8">Our team will review your request and contact you within 24-48 hours with pricing and availability.</p>
          <Button onClick={() => window.location.href = '/'} className="bg-green-600 hover:bg-green-700">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="wholesale-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-green-800 via-green-700 to-emerald-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="max-w-7xl mx-auto px-4 py-20 relative">
          <div className="max-w-2xl">
            <span className="inline-block bg-white/20 text-white text-sm font-medium px-4 py-1 rounded-full mb-4">
              B2B Wholesale Program
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Partner With Daye Farm<br />For Bulk Supply
            </h1>
            <p className="text-xl text-green-100 mb-8">
              Direct farm-to-business supply of premium agricultural products at competitive wholesale prices.
            </p>
            <div className="flex flex-wrap gap-6 text-sm text-green-200">
              <span className="flex items-center gap-2"><Truck className="h-4 w-4" /> Nationwide Delivery</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4" /> Flexible Schedules</span>
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Quality Guaranteed</span>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-12">Why Partner With Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: Package, title: 'Bulk Pricing', desc: 'Competitive rates for volume orders with custom price negotiations' },
              { icon: Truck, title: 'Reliable Delivery', desc: 'On-time delivery with real-time tracking nationwide' },
              { icon: Globe, title: 'Wide Range', desc: 'Livestock, crops, fish, processed goods, and farm inputs' },
              { icon: Users, title: 'Dedicated Support', desc: 'Personal account manager for your business needs' },
            ].map((item) => (
              <Card key={item.title} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6 pb-4">
                  <div className="w-14 h-14 bg-green-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <item.icon className="h-7 w-7 text-green-600" />
                  </div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="py-16 bg-gray-50" id="inquiry-form">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-3">Request a Wholesale Quote</h2>
            <p className="text-gray-600">Fill out the form below and our team will prepare a custom quote for your business.</p>
            {requestedProduct && <p className="mt-3 text-sm font-medium text-emerald-800" data-testid="prefilled-quote-product">Quote request prepared for {requestedProduct}{requestedQuantity ? ` · ${requestedQuantity}` : ''}</p>}
          </div>

          <Card>
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Business Info */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-green-600" /> Business Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Business Name *</label>
                      <Input value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})} required placeholder="Your company name" data-testid="wh-business-name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Business Type</label>
                      <select value={form.business_type} onChange={e => setForm({...form, business_type: e.target.value})} className="w-full border rounded-md px-3 py-2 text-sm" data-testid="wh-business-type">
                        <option value="">Select type</option>
                        <option value="restaurant">Restaurant / Hotel</option>
                        <option value="supermarket">Supermarket / Store</option>
                        <option value="distributor">Distributor</option>
                        <option value="manufacturer">Food Manufacturer</option>
                        <option value="exporter">Exporter</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Contact */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Phone className="h-5 w-5 text-green-600" /> Contact Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Contact Person *</label>
                      <Input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} required placeholder="Full name" data-testid="wh-contact-name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Phone *</label>
                      <div className="flex gap-0">
                        <div className="relative">
                          <select
                            value={dialCode}
                            onChange={(e) => setDialCode(e.target.value)}
                            className="h-10 pl-3 pr-7 border border-r-0 rounded-l-md bg-gray-50 text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500"
                            data-testid="wh-dial-code"
                          >
                            {countryCodes.map(c => (
                              <option key={c.code} value={c.code}>{c.code} {c.country}</option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                        </div>
                        <Input
                          value={form.phone}
                          onChange={e => setForm({...form, phone: e.target.value.replace(/\D/g, '')})}
                          required
                          placeholder="Phone number"
                          className="rounded-l-none flex-1"
                          data-testid="wh-phone"
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">Email *</label>
                      <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required placeholder="business@example.com" data-testid="wh-email" />
                    </div>
                  </div>
                </div>

                {/* Products */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Package className="h-5 w-5 text-green-600" /> Products of Interest
                  </h3>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">What products are you interested in? *</label>
                    <textarea
                      value={form.products_interested}
                      onChange={e => setForm({...form, products_interested: e.target.value})}
                      required
                      placeholder="Ginger, Cashew nuts, Sesame seeds, Hibiscus flower, etc."
                      className="w-full border rounded-md px-3 py-2 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      data-testid="wh-products-interested"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Estimated Quantity</label>
                      <Input value={form.estimated_quantity} onChange={e => setForm({...form, estimated_quantity: e.target.value})} placeholder="e.g., 500kg weekly" data-testid="wh-quantity" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Delivery Frequency</label>
                      <select value={form.delivery_frequency} onChange={e => setForm({...form, delivery_frequency: e.target.value})} className="w-full border rounded-md px-3 py-2 text-sm" data-testid="wh-frequency">
                        <option value="">Select frequency</option>
                        <option value="one_time">One-time Order</option>
                        <option value="weekly">Weekly</option>
                        <option value="bi_weekly">Bi-weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="custom">Custom Schedule</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Delivery */}
                <div>
                  <label className="block text-sm font-medium mb-1">Delivery Location</label>
                  <Input value={form.delivery_location} onChange={e => setForm({...form, delivery_location: e.target.value})} placeholder="City, State" data-testid="wh-location" />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Additional Notes</label>
                  <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={3} className="w-full border rounded-md px-3 py-2 text-sm resize-none" placeholder="Any special requirements..." data-testid="wh-message" />
                </div>

                <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 h-12 text-base" disabled={submitting} data-testid="wh-submit-btn">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Send className="h-5 w-5 mr-2" />}
                  Submit Wholesale Inquiry
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Wholesale;
